import { ActionIcon, Box, Collapse, Group, Loader, Tooltip } from '@mantine/core';
import { useDisclosure, useHover } from '@mantine/hooks';
import { openModal } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import cx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { queryBotDatasetsList, useBot, useBotStore, useCollaborateStore } from '@mygpt/app';
import { SvgSource, SvgSourceFilled } from './icon';
import { SvgArrow } from '@/components/Widget/icon';
import { BindContent } from '@/components/Embeddings/BindContent';
import { useGPTStore } from '@/store';
import { DatasetNavItem } from './datasetItem';

export const DatasetNav = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { '*': params } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { linkedDataset, setLinkedDataset } = useBotStore();
  const { isAssist } = useCollaborateStore();
  const { userInfo, robotInfo, assetScope } = useGPTStore();

  // 模式判断
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudEnterprise = !isLocalMode && !!assetScope;

  // 权限判断：本地部署和Cloud企业模式下判断是否为OWNER
  const isOwner = useMemo(() => {
    if (isCloudEnterprise) {
      if (robotInfo?.permission_type) {
        return robotInfo.permission_type === 'OWNER';
      }
      return userInfo?.user_id === robotInfo?.user?.user_id;
    }
    if (isLocalMode) {
      return userInfo?.email === robotInfo?.user?.email;
    }
    return true;
  }, [
    isLocalMode,
    isCloudEnterprise,
    userInfo?.user_id,
    userInfo?.email,
    robotInfo?.user?.user_id,
    robotInfo?.user?.email,
    robotInfo?.permission_type,
  ]);

  // 判断是否应该隐藏操作按钮（isAssist模式或非owner用户）
  const shouldHideActions = useMemo(() => {
    return isAssist || !isOwner;
  }, [isAssist, isOwner]);

  const [initList, setInitList] = useState(true);
  const [opened, { toggle }] = useDisclosure(true);

  const { hovered, ref } = useHover();

  const isDatasetNavActive = useMemo(() => {
    return /^(bot|assist)\/[0-9a-fA-F-]+\/data-source$/.test(params || '');
  }, [params]);

  const currentDatasetId = useMemo(() => {
    const match = params?.match(/datasets\/([^/]+)\/data-source/);
    return match && match[1] ? match[1] : '';
  }, [params]);

  const init = async ({ showLoading, unbindId }: { showLoading?: boolean; unbindId?: string }) => {
    showLoading && setInitList(true);
    const res = await queryBotDatasetsList({
      aiId: botId,
      page: 1,
      size: 1000,
    });
    setInitList(false);
    setLinkedDataset(res.data.items);

    // 如果移除了当前选中的知识库，则请求完成后默认选中第一个
    if (unbindId === currentDatasetId) {
      navigate(pathname.replace(currentDatasetId, res.data.items[0].id));
    }
  };

  useEffect(() => {
    botId && init({ showLoading: true });
  }, [botId]);

  const isCloning = useMemo(() => {
    return !!linkedDataset.some((item) => ['cloning_target', 'cloning_source'].includes(item.data_status));
  }, [linkedDataset]);

  useEffect(() => {
    if (!isCloning) return;

    const interval = setInterval(() => {
      init({ showLoading: false });
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [isCloning]);

  const openAddModal = () => {
    openModal({
      title: t('embeddings.dataset.add_modal.desc'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: (
        <BindContent
          id={botId}
          initList={initList}
          onBind={() => {
            init({ showLoading: true });
          }}
        />
      ),
    });
  };

  return (
    <>
      <Box
        ref={ref}
        className={`group flex items-center justify-between px-3 py-2 rounded-lg ${
          isDatasetNavActive
            ? 'bg-violet-100 text-violet-500'
            : 'cursor-pointer hover:bg-violet-50 hover:text-violet-500'
        } `}
      >
        <Group gap={6} wrap="nowrap">
          {/* 有关联数据集时才展示箭头 */}
          {linkedDataset.length > 0 && (
            <span className={cx(opened && 'rotate-180', 'transition-all', 'cursor-pointer')} onClick={toggle}>
              <SvgArrow />
            </span>
          )}
          <Group
            gap={8}
            wrap="nowrap"
            onClick={() => {
              navigate(`/${isAssist ? 'assist' : 'bot'}/${botId}/data-source`);
            }}
          >
            {hovered || isDatasetNavActive ? <SvgSourceFilled /> : <SvgSource />}
            <span className="text-sm">{t('menu.bot.data_sources')}</span>
          </Group>
        </Group>

        {!shouldHideActions && (
          <Tooltip label={t('embeddings.dataset.add')} position="right" withArrow>
            <ActionIcon
              size="sm"
              variant="subtle"
              className="invisible group-hover:visible"
              onClick={() => {
                openAddModal();
              }}
            >
              <IconPlus />
            </ActionIcon>
          </Tooltip>
        )}
      </Box>

      <Collapse in={opened} transitionDuration={400} className="pl-10">
        {initList ? (
          <Group justify="center" className="pt-3 pr-10">
            <Loader color="violet" size="xs" />
          </Group>
        ) : (
          linkedDataset.map((item, index) => {
            return (
              <DatasetNavItem
                key={index}
                item={item}
                currentDatasetId={currentDatasetId}
                botId={botId}
                handleUnbind={(id) => {
                  init({ showLoading: true, unbindId: id });
                }}
              />
            );
          })
        )}
      </Collapse>
    </>
  );
};
